import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { api } from '../api/client.js';

const initialState = {
  metrics: null,
  // When `metrics` was answered (Date.now()); a page can tell a count fetched
  // before it opened from one fetched since
  fetchedAt: null,
  loading: false,
  error: null,
  intervalId: null,
  onVisible: null,
};

export const useUsageStore = create(
  subscribeWithSelector(
    devtools((set, get) => ({
      ...initialState,
      setLoading: (loading) => set({ loading }),
      setMetrics: (metrics) => set({ metrics }),
      setError: (error) => set({ error }),

      fetchUsage: async () => {
        set({ loading: true, error: null });
        try {
          const data = await api.getUsageMetrics();
          set({ metrics: data, fetchedAt: Date.now() });
        } catch (error) {
          console.error('UsageStore: Failed to fetch usage metrics', error);
          set({ error: error?.message || 'Failed to load usage metrics' });
        } finally {
          set({ loading: false });
        }
      },

      // Asked every minute only while the page is in front: a tab left in the background
      // asked all day (25k /usage/metrics a day on Cloudflare, 2026-10-16). Coming back
      // to a tab asks at once when the last answer is older than the interval
      startPolling: (intervalMs = 60000) => {
        const current = get().intervalId;
        if (current) return current;
        const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const id = setInterval(() => {
          if (!hidden()) get().fetchUsage();
        }, intervalMs);
        const onVisible = () => {
          if (!hidden() && Date.now() - (get().fetchedAt || 0) >= intervalMs) get().fetchUsage();
        };
        if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
        set({ intervalId: id, onVisible });
        return id;
      },

      stopPolling: () => {
        const { intervalId: id, onVisible } = get();
        if (id) {
          clearInterval(id);
        }
        if (onVisible && typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
        set({ intervalId: null, onVisible: null });
      },

      reset: () => {
        get().stopPolling();
        set(initialState);
      }
    }), { name: 'UsageStore' })
  )
);

export const selectUsageState = (state) => ({
  metrics: state.metrics,
  fetchedAt: state.fetchedAt,
  loading: state.loading,
  error: state.error,
});
